<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed, onMounted, ref, watch } from "vue";
import { AtomLoadingSpinner, SatItemDisplay } from "@chatfood/design-system";
import type { ICrossSellItemsEmits } from "./emits";
import type { ICrossSellItemsProps } from "./props";
import {
  useAnalytics,
  useBugReporter,
  useDirectBackend,
  type IDomainMoney,
} from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import { useNavigation } from "~/composables/use-navigation";

const css = bemBuilder("cross-sell-items");
const props = defineProps<ICrossSellItemsProps>();
const emits = defineEmits<ICrossSellItemsEmits>();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const { basketRepo } = useDirectBackend();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { rawPush } = useNavigation();

const hasTrackedImpression = ref(false);

const SURFACE_BY_VARIANT: Record<
  NonNullable<ICrossSellItemsProps["variant"]>,
  "modal" | "checkout"
> = {
  list: "modal",
  "scroll-x": "checkout",
};

const crossSellItems = ref<
  Awaited<ReturnType<typeof basketRepo.getCrossSellItems>>["data"]
>([]);

const discountedProducts = computed(() => {
  const discount = basket.discount;

  if (!discount) return {};

  if (discount.unit !== "percentage") return {};

  const items = crossSellItems.value.flat();

  const products = discount.discountedProducts;
  const discountAmount = discount.amount;

  return items.reduce(
    (acc, item) => {
      const isItemDiscounted =
        products.length === 0 || products.includes(item.plu);
      const price = !isItemDiscounted
        ? item.price
        : {
            value: item.price.value * (1 - discountAmount / 100),
            currency: item.price.currency,
          };

      return {
        ...acc,
        [item.plu]: price,
      };
    },
    {} as Record<string, IDomainMoney>,
  );
});

const isLoading = ref(true);

const fetchCrossSellItems = async () => {
  if (crossSellItems.value.length === 0) {
    isLoading.value = true;
  }

  try {
    const res = await basketRepo.getCrossSellItems({
      businessId: business.id,
      outletId: outlet.id,
      orderingMode: basket.orderingMode,
      salesChannel: basket.salesChannel,
    });

    // Filter out duplicates based on item id
    const uniqueItems = res.data.filter(
      (item, index, self) => index === self.findIndex((i) => i.id === item.id),
    );

    crossSellItems.value = uniqueItems;
    emits("loaded", uniqueItems.length);

    if (!hasTrackedImpression.value && uniqueItems.length > 0) {
      hasTrackedImpression.value = true;
      trackEvent({
        name: "viewCrossSell",
        payload: {
          surface: props.variant
            ? SURFACE_BY_VARIANT[props.variant]
            : "checkout",
          itemCount: uniqueItems.length,
        },
      });
    }
  } catch (e) {
    emits("loaded", 0);
    report(e);
  } finally {
    isLoading.value = false;
  }
};

const handleItemClick = (id: string) => {
  rawPush({
    query: {
      "item-id": id,
      "item-flow": "cross-sell",
    },
  });
};

watch(
  () => basket.items,
  (newItems, oldItems) => {
    if (newItems.length !== oldItems.length) {
      fetchCrossSellItems();
    }
  },
  { deep: true },
);

onMounted(() => {
  fetchCrossSellItems();
});
</script>

<template>
  <div :class="[css(''), css('', props.variant)]">
    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <template v-else>
      <SatItemDisplay
        v-for="item in crossSellItems"
        :id="item.id"
        :key="item.id"
        :class="css('items')"
        view="col"
        :name="item.name"
        :original-amount="item.price"
        :description="item.description"
        :amount="discountedProducts[item.plu] || item.price"
        :photo="item.photo"
        :has-allergen-items="Boolean(item.allergens)"
        :consumables="item.consumables"
        @click="handleItemClick(item.id)"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.cross-sell-items {
  display: flex;
  gap: var(--spacing-md);
  width: 100%;

  &--list {
    flex-direction: column;
  }

  &--scroll-x {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: 12px;
  }

  &__items {
    min-width: 90%;
    width: 100%;
    border: 1px solid var(--color-base-80);
    border-radius: var(--radii-card-md);
    padding: var(--spacing-md);
    cursor: pointer;
  }

  &__loading {
    margin: var(--spacing-3xl) auto;
    display: flex;
  }
}
</style>
